import type { HeavenlyStem } from '../types/stems';
import type { Pillar, FourPillars } from '../types/pillars';
import type { FortuneDirection, MajorFortune, MajorFortuneEntry } from '../types/fortune';
import { nextPillar, prevPillar } from '../core/sexagenary';
import { isStemYang } from '../core/eumyang';
import { dateToJD } from '../astronomy/julian-day';
import { findNextJeolgi, findPreviousJeolgi } from '../astronomy/solar-terms';
import type { YongSin } from '../types/strength';
import type { GyeokGukAnalysis } from '../types/gyeok-guk';
import { getTenGod } from '../analysis/ten-gods';
import { analyzeFortuneRelations } from '../relations/fortune-relations';

/**
 * 대운 방향을 결정한다.
 *
 * 양남음녀 = 순행(forward), 음남양녀 = 역행(reverse)
 *
 * @param yearStem - 년주 천간
 * @param gender - 성별 ('male' | 'female')
 * @returns 'forward' (순행) 또는 'reverse' (역행)
 */
export function getFortuneDirection(yearStem: HeavenlyStem, gender: 'male' | 'female'): FortuneDirection {
  const isYang = isStemYang(yearStem);
  const isMale = gender === 'male';

  // 양남 or 음녀 = forward, 음남 or 양녀 = reverse
  if ((isYang && isMale) || (!isYang && !isMale)) {
    return 'forward';
  }
  return 'reverse';
}

/**
 * 대운 시작 나이를 계산한다.
 *
 * 출생일에서 다음(순행) 또는 이전(역행) 절(節)까지의 일수를 센 후
 * 3으로 나눈다 (1일 ≈ 4개월).
 *
 * @returns 대운 시작 나이 (소수점 가능)
 */
export function calculateFortuneStartAge(
  year: number, month: number, day: number,
  hour: number, minute: number,
  direction: FortuneDirection
): number {
  const birthJD = dateToJD(year, month, day, hour, minute);

  let targetJeolgi;
  if (direction === 'forward') {
    targetJeolgi = findNextJeolgi(birthJD, year);
  } else {
    targetJeolgi = findPreviousJeolgi(birthJD, year);
  }

  if (!targetJeolgi) {
    return 1; // fallback
  }

  const daysDiff = Math.abs(targetJeolgi.jd - birthJD);

  // 정운법: 3일 = 1년 비례, 나머지 < 2일 버림, ≥ 2일 올림
  const quotient = Math.floor(daysDiff / 3);
  const remainder = daysDiff % 3;
  const startAge = remainder >= 2 ? quotient + 1 : quotient;

  // 대운수 최소 1 (0세 대운은 없음)
  return Math.max(startAge, 1);
}

/**
 * 대운 배열을 계산한다.
 *
 * @param monthPillar - 원국 월주
 * @param dayMaster - 일간 (십신 계산용)
 * @param direction - 순행 또는 역행
 * @param startAge - 대운 시작 나이
 * @param birthYear - 출생 연도 (시작 연도 산출용)
 * @param count - 생성할 대운 개수 (기본값: 10)
 */
export function calculateMajorFortune(
  monthPillar: Pillar,
  dayMaster: HeavenlyStem,
  direction: FortuneDirection,
  startAge: number,
  birthYear: number,
  count: number = 10,
  fourPillars?: FourPillars,
  noHour?: boolean,
  yongSin?: YongSin,
  gyeokGuk?: GyeokGukAnalysis,
): MajorFortune {
  const entries: MajorFortuneEntry[] = [];

  for (let i = 1; i <= count; i++) {
    const pillar = direction === 'forward'
      ? nextPillar(monthPillar, i)
      : prevPillar(monthPillar, i);

    const entryStartAge = Math.round(startAge + (i - 1) * 10);
    const entryEndAge = entryStartAge + 9;
    // 명리학 나이는 세는나이(출생년=1세)이므로 연도 = 출생년 + 나이 - 1
    const startYear = birthYear + entryStartAge - 1;
    const endYear = startYear + 9;

    entries.push({
      pillar,
      startAge: entryStartAge,
      endAge: entryEndAge,
      startYear,
      endYear,
      tenGod: getTenGod(dayMaster, pillar.stem),
      relations: fourPillars ? analyzeFortuneRelations(fourPillars, pillar, noHour, yongSin, gyeokGuk) : [],
    });
  }

  return {
    direction,
    startAge,
    entries,
  };
}
